import axios from 'axios';

// Get the API URL from environment variables, fallback to local development server URL
const BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// Configure Axios request interceptor to attach JWT bearer token
axios.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Configure Axios response interceptor to handle session expiry (401 Unauthorized)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 from the auth endpoints themselves (e.g. a wrong-password login) is a
    // normal validation error that the caller surfaces inline — it is NOT an expired
    // session, so don't clear storage or reload the page (which would close the login
    // popup before the error can be shown).
    const requestUrl = error.config?.url || '';
    const isAuthEndpoint = requestUrl.includes('/api/auth/');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Token expired or invalid; clear session and redirect to login
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      // Reload to trigger login screen
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

/**
 * Handles user login and stores the JWT token.
 */
export async function login(email, password) {
  const response = await axios.post(`${BASE_URL}/api/auth/login`, { email, password });
  if (response.data && response.data.access_token) {
    sessionStorage.setItem('token', response.data.access_token);
    sessionStorage.setItem('user', JSON.stringify(response.data.user));
  }
  return response.data;
}

/**
 * Submits an access request. The account is created in a pending state — no session is
 * started; the user can log in once an admin approves them.
 */
export async function signup(email, password, name, organization, phone, designation) {
  const response = await axios.post(`${BASE_URL}/api/auth/signup`, { email, password, name, organization, phone, designation });
  return response.data;
}

/**
 * Admin: user access management. All endpoints require an admin session.
 */
export async function adminListUsers(includeDeleted = false) {
  const response = await axios.get(`${BASE_URL}/api/admin/users`, {
    params: { include_deleted: includeDeleted },
  });
  return response.data;
}

export async function adminApproveUser(userId) {
  const response = await axios.post(`${BASE_URL}/api/admin/users/${userId}/approve`);
  return response.data;
}

export async function adminRejectUser(userId) {
  const response = await axios.post(`${BASE_URL}/api/admin/users/${userId}/reject`);
  return response.data;
}

export async function adminDisableUser(userId) {
  const response = await axios.post(`${BASE_URL}/api/admin/users/${userId}/disable`);
  return response.data;
}

export async function adminEnableUser(userId) {
  const response = await axios.post(`${BASE_URL}/api/admin/users/${userId}/enable`);
  return response.data;
}

/**
 * Soft delete: the account is hidden and blocked from logging in, but its data is
 * retained and adminEnableUser() restores it.
 */
export async function adminDeleteUser(userId) {
  const response = await axios.delete(`${BASE_URL}/api/admin/users/${userId}`);
  return response.data;
}

export async function adminUpdateUserPermissions(userId, permissions) {
  const response = await axios.patch(`${BASE_URL}/api/admin/users/${userId}/permissions`, permissions);
  return response.data;
}

export async function adminGetFeedbackStats() {
  const response = await axios.get(`${BASE_URL}/api/admin/feedback/stats`);
  return response.data;
}

export async function adminGetGuardrailsStats() {
  const response = await axios.get(`${BASE_URL}/api/admin/guardrails/stats`);
  return response.data;
}

/**
 * Requests a password reset email. Always resolves with a generic message.
 */
export async function forgotPassword(email) {
  const response = await axios.post(`${BASE_URL}/api/auth/forgot-password`, { email });
  return response.data;
}

/**
 * Completes a password reset using the token from the emailed link.
 */
export async function resetPassword(token, newPassword) {
  const response = await axios.post(`${BASE_URL}/api/auth/reset-password`, { token, new_password: newPassword });
  return response.data;
}

/**
 * Validates the current session and gets the user profile.
 */
export async function getCurrentUser() {
  const response = await axios.get(`${BASE_URL}/api/auth/me`);
  return response.data;
}

/**
 * Clears local authenticated session data.
 */
export function logout() {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
}

/**
 * Retrieves the list of files uploaded in the user's workspace.
 */
export async function getFiles() {
  const response = await axios.get(`${BASE_URL}/files`);
  return response.data;
}

/**
 * Retrieves the historical analysis reports run by the user.
 */
export async function getReports() {
  const response = await axios.get(`${BASE_URL}/reports`);
  return response.data;
}

/**
 * Uploads a QA artifact file to the backend vector database.
 * 
 * @param {File} file - The file object to upload (PDF, DOCX, XLSX, or CSV).
 * @param {string} artifactType - The category of the artifact ('requirement', 'bug_report', or 'test_case').
 * @returns {Promise<Object>} The server response details about the uploaded document.
 */
export async function uploadFile(file, artifactType, isPrevious = false) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('artifact_type', artifactType);
  formData.append('is_previous', isPrevious ? 'true' : 'false');

  const response = await axios.post(`${BASE_URL}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * Performs AI-based risk analysis on the uploaded documents.
 * 
 * @param {string[]} artifactTypes - List of artifact types to include in analysis (e.g. ['requirement', 'bug_report', 'test_case']).
 * @param {string} customInstruction - Optional specific instructions/query from the user.
 * @param {string[]} currentDocumentIds - List of active current document IDs.
 * @param {string[]} historicalDocumentIds - List of active historical document IDs.
 * @returns {Promise<Object>} The detailed structured risk assessment results from the AI engine.
 */
export async function analyseRisk(artifactTypes, customInstruction = '', currentDocumentIds = null, historicalDocumentIds = null, saveToHistorical = false, includeCodeImpact = false) {
  let question = 'What is at risk in this release? Identify high-risk requirements, coverage gaps, and recurring bug patterns.';
  if (customInstruction && customInstruction.trim()) {
    question += ` Specific User Instruction: ${customInstruction.trim()}`;
  }

  const response = await axios.post(`${BASE_URL}/query`, {
    question: question,
    artifact_types: artifactTypes.length > 0 ? artifactTypes : null,
    current_document_ids: currentDocumentIds,
    historical_document_ids: historicalDocumentIds,
    save_to_historical: saveToHistorical,
    include_code_impact: includeCodeImpact,
  });
  return response.data;
}

/**
 * Performs Multi-Agent Orchestrated Risk Analysis on uploaded documents via /query-v2.
 */
export async function analyseRiskV2(artifactTypes, customInstruction = '', currentDocumentIds = null, historicalDocumentIds = null, saveToHistorical = false, includeCodeImpact = false) {
  let question = 'What is at risk in this release? Identify high-risk requirements, coverage gaps, and recurring bug patterns.';
  if (customInstruction && customInstruction.trim()) {
    question += ` Specific User Instruction: ${customInstruction.trim()}`;
  }

  const response = await axios.post(`${BASE_URL}/query-v2`, {
    question: question,
    artifact_types: artifactTypes.length > 0 ? artifactTypes : null,
    current_document_ids: currentDocumentIds,
    historical_document_ids: historicalDocumentIds,
    save_to_historical: saveToHistorical,
    include_code_impact: includeCodeImpact,
  });
  return response.data;
}


/**
 * Connects a GitHub repository for live code-impact search during analysis. The token is sent
 * once to be validated and stored encrypted server-side; it is not persisted in the browser.
 */
export async function connectGithub(token, repoFullName) {
  const response = await axios.post(`${BASE_URL}/api/github/connect`, { token, repo_full_name: repoFullName });
  return response.data;
}

export async function getGithubStatus() {
  const response = await axios.get(`${BASE_URL}/api/github/status`);
  return response.data;
}

export async function disconnectGithub() {
  const response = await axios.post(`${BASE_URL}/api/github/disconnect`);
  return response.data;
}

/**
 * Jira Cloud connection. The API token is sent once to be validated and stored
 * encrypted server-side; it is never persisted in the browser.
 */
export async function connectJira(baseUrl, accountEmail, token) {
  const response = await axios.post(`${BASE_URL}/api/jira/connect`, {
    base_url: baseUrl,
    account_email: accountEmail,
    token,
  });
  return response.data;
}

export async function getJiraStatus() {
  const response = await axios.get(`${BASE_URL}/api/jira/status`);
  return response.data;
}

export async function disconnectJira() {
  const response = await axios.post(`${BASE_URL}/api/jira/disconnect`);
  return response.data;
}

/**
 * Fetch a Jira story (by key like "ABC-123" or a browse URL) and ingest its
 * summary + description as the current requirement.
 */
export async function fetchJiraStory(story, isPrevious = false) {
  const response = await axios.post(`${BASE_URL}/api/jira/fetch-story`, { story, is_previous: isPrevious });
  return response.data;
}

/**
 * Saved JQL queries (max 5 per user) and defect fetch. A fetch runs the JQL (up to
 * 50 issues) and ingests the result set as one bug_report document — replacing the
 * current bug report, or adding to the Historical KB when isPrevious is true.
 */
export async function listJiraQueries() {
  const response = await axios.get(`${BASE_URL}/api/jira/queries`);
  return response.data;
}

export async function saveJiraQuery(jql, name) {
  const response = await axios.post(`${BASE_URL}/api/jira/queries`, { jql, name: name || null });
  return response.data;
}

export async function deleteJiraQuery(queryId) {
  const response = await axios.delete(`${BASE_URL}/api/jira/queries/${queryId}`);
  return response.data;
}

export async function fetchJiraDefects(jql, isPrevious = false) {
  const response = await axios.post(`${BASE_URL}/api/jira/fetch-defects`, { jql, is_previous: isPrevious });
  return response.data;
}

/**
 * Extracted text of a stored document, so the contents can be verified against the
 * source. Text is re-extracted server-side (uploads are stored as original binaries).
 */
export async function previewDocument(documentId) {
  const response = await axios.get(`${BASE_URL}/api/documents/${documentId}/preview`);
  return response.data;
}

/**
 * Runs the agentic test-generation pipeline for a completed analysis: a Test Case Writer agent
 * turns identified risks/gaps into structured test cases, then an Automation Engineer agent
 * writes Playwright specs for the UI-testable ones. Takes ~30-60s (two sequential LLM calls).
 */
export async function generateTestCases(reportId) {
  const response = await axios.post(`${BASE_URL}/generate-tests/cases`, { report_id: reportId }, { timeout: 180000 });
  return response.data;
}

export async function generateTestAutomation(reportId, testCases, testData = null) {
  const response = await axios.post(`${BASE_URL}/generate-tests/automation`, { report_id: reportId, test_cases: testCases, test_data: testData }, { timeout: 180000 });
  return response.data;
}

/**
 * Agent 3 (Test Data Generator): generates realistic QA datasets (positive, negative,
 * boundary, and security values) for each test case Agent 1 produced, grounded in the
 * requirements and bug context. Returns per-TC JSON files and a consolidated CSV.
 *
 * Designed to run in parallel with Agent 2 once Agent 1's test cases are available.
 */
export async function generateTestData(reportId, testCases) {
  const response = await axios.post(`${BASE_URL}/generate-tests/test-data`, { report_id: reportId, test_cases: testCases }, { timeout: 180000 });
  return response.data;
}

/**
 * Records a thumbs up/down verdict on a generated artifact ('risk_report', 'test_cases',
 * or 'automation') for a given report. Re-submitting for the same report+feature updates
 * the existing verdict rather than creating a duplicate. `reason` is only used on 'down';
 * `comment` is optional free text and applies regardless of verdict.
 */
export async function submitFeedback(reportId, feature, verdict, reason = null, comment = null) {
  const response = await axios.post(`${BASE_URL}/api/feedback`, {
    report_id: reportId,
    feature,
    verdict,
    reason,
    comment,
  });
  return response.data;
}

/**
 * Retrieves this user's existing feedback for a report, keyed by feature, so the UI can
 * restore which thumb was previously selected.
 */
export async function getFeedback(reportId) {
  const response = await axios.get(`${BASE_URL}/api/feedback/${reportId}`);
  return response.data;
}

/**
 * Deletes a QA artifact file and its chunks from the backend vector database.
 * 
 * @param {string} documentId - The unique ID of the document to delete.
 * @returns {Promise<Object>} Response from the server.
 */
export async function deleteFile(documentId) {
  const response = await axios.post(`${BASE_URL}/delete/${documentId}`);
  return response.data;
}

/**
 * Returns per-artifact-type file counts and capacity status for the calling user's
 * Historical Knowledge Base. Used by both the automatic-save pre-analysis check (Flow 1)
 * and the manual Historical KB upload pre-check (Flow 2) to surface limit modals instead
 * of silent failures.
 *
 * @returns {Promise<Object>} Capacity status object, e.g.
 *   {
 *     requirement: { current_count: 10, max_allowed: 10, insertion_allowed: false, label: "Requirement Documents" },
 *     bug_report:  { current_count: 7,  max_allowed: 10, insertion_allowed: true,  label: "Bug Reports" },
 *     test_case:   { current_count: 5,  max_allowed: 10, insertion_allowed: true,  label: "Test Case Reports" },
 *     any_full: true,
 *     full_types: ["requirement"]
 *   }
 */
export async function getKbCapacity() {
  const response = await axios.get(`${BASE_URL}/api/kb/capacity`);
  return response.data;
}

